import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import sortBy from 'lodash/sortBy';
import { generateText } from '@tiptap/core';

import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import {
  ActivityBuilderDrawerContent,
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerWrapper,
} from 'approot/activity-builder/activity-builder-drawer';
import {
  SettingsGroup,
  SettingsHr,
  SettingSmallText,
} from 'approot/activity-builder/settings/settings.style';
import {
  BoldStyled,
  FONT_CALIBRE_SCHOOL,
  FONT_NSW_HANDWRITING,
  fontSize16,
  fontSize16LineHeight,
  PStyled,
} from 'approot/shared/styles/typography.style';
import {
  SecondaryButton,
  SecondaryButtonIcon,
} from 'approot/shared/styles/buttons/buttons';
import { getReorderSort } from 'approot/shared/sortable/sortable.utils';
import {
  Sortable,
  SortableHeader,
  SortableItem,
} from 'approot/shared/sortable/sortable';
import { useSortableScrollManager } from 'approot/shared/sortable/sortable.hooks';
import { SORTABLE_CARD } from 'approot/shared/sortable/sortable.constants';
import { MultiViewProps } from 'approot/shared/layout/layout-multi-view';
import { useBatchComponentUpdateContext } from '../../shared/batch-component-update.context';
import customNanoId from 'lib/nanoid/nanoid';
import { DIRECTION_LEFT } from 'webclient.constants';
import { SortableContentButton } from 'approot/shared/sortable/sortable.style';
import { Box } from 'approot/shared/design-system/box/box.style';
import {
  FILL_IN_BLANKS_SENTENCE,
  FILL_IN_BLANKS_SENTENCE_DETAILS,
} from './fill-in-blanks-settings.constants';
import {
  BASE_FILL_IN_BLANKS_TEXT_BLOCK_SETTINGS,
  FillInBlanksSentence,
  SettingsProps,
} from '../fill-in-blanks.constants';
import { useFillInBlanksSettingsContext } from './fill-in-blanks-settings.context';
import { ActivityBuilderModal } from 'approot/activity-builder/activity-builder-modal';
import { NegativeButton } from 'approot/shared/styles/buttons';
import useFillInBlanksDeleteSentence from './use-fill-in-blanks-delete-sentence';
import { mergeFillInBlanksBlocks } from '../logic/merge-fill-in-blanks-blocks';
import { COLOUR_GREY } from 'approot/shared/colour/colour.constants';
import * as Styled from './fill-in-blanks-settings-sentences.style';
import {
  getExtensions,
  getFontFamilyLabel,
} from 'approot/shared/richtext/richtext.utils';
import { Dropdown, DropdownIcon } from 'approot/shared/dropdown/dropdown';
import RichtextToolbarDropdownItem from 'approot/shared/richtext/richtext-toolbar-dropdown-item';
import { grey10 } from 'approot/shared/styles/colours.style';
import { FontFamily } from 'approot/shared/richtext/richtext-toolbar.constants';
import { isRichtextValueEmpty } from '../../shared/logic/get-richtext-value';
import IconTextResize from 'approot/shared/icons/icon-text-resize';
import {
  ACTIVITIES_OPTION_SIZE_HUGE,
  ACTIVITIES_OPTION_SIZE_LARGE,
  ACTIVITIES_OPTION_SIZE_MEDIUM,
} from 'global/constants';
import IconTrash from 'approot/shared/icons/icon-trash';
import IconDropdown from 'approot/shared/icons/icon-dropdown';

const FillInBlanksSettingsSentences = ({
  sectionId,
  id,
  type,
  properties,
  suggestedAnswerProperties,
  onCloseChildDrawer,
  goTo,
}: SettingsProps & MultiViewProps) => {
  const ref = useRef<HTMLElement | null>(null);

  const [sentenceItemId, setSentenceItemId] = useState<string>();

  const fontFamily = properties.fillInBlanks.fontFamily as FontFamily;
  const fontSize = properties.fillInBlanks.fontSize;

  const { updateComponentProperties } = useBatchComponentUpdateContext();
  const { handleDeleteSentence } = useFillInBlanksDeleteSentence({
    id,
    sectionId,
    properties,
    suggestedAnswerProperties,
  });
  const { setActiveSentence, setActiveAnswer } =
    useFillInBlanksSettingsContext();

  const { editModeTargetElement, closeDrawer, setEditModeTargetElement } =
    useActivityBuilderContext();

  useSortableScrollManager(ref, [SORTABLE_CARD]);

  const sortedSentences = useMemo(
    () => sortBy(properties.fillInBlanks.sentences, 'sort'),
    [properties.fillInBlanks.sentences]
  );

  const handleAddSentence = useCallback(() => {
    const sentenceSort = getReorderSort(
      sortedSentences,
      sortedSentences.length,
      DIRECTION_LEFT
    );
    const newSentence: FillInBlanksSentence = {
      __typename: 'FillInBlanksSentence',
      itemId: customNanoId(),
      sort: sentenceSort,
      textBlocks: [
        {
          __typename: 'FillInBlanksText',
          itemId: customNanoId(),
          sort: 'V',
          text: {
            type: 'doc',
            content: [
              {
                type: 'paragraph',
                content: [
                  {
                    type: 'text',
                    text: 'Text',
                  },
                ],
              },
            ],
          },
        },
      ],
      answerBlocks: [
        {
          __typename: 'FillInBlanksAnswer',
          itemId: customNanoId(),
          sort: 'k',
          colour: COLOUR_GREY.base,
          label: null,
          placeholder: null,
          punctuation: null,
          showLabel: false,
          showPlaceholderAsScaffold: false,
          width: 100,
        },
      ],
    };

    updateComponentProperties({
      input: {
        componentId: id,
        sectionId,
        componentData: {
          type,
          properties: {
            fillInBlanks: {
              ...properties.fillInBlanks,
              sentences: [...sortedSentences, newSentence],
            },
          },
        },
      },
      properties: {
        ...properties,
        fillInBlanks: {
          ...properties.fillInBlanks,
          sentences: [...sortedSentences, newSentence],
        },
      },
      typename: 'ActivitiesFillInBlanksResponseComponent',
    });
    setActiveSentence({
      itemId: newSentence.itemId,
    });
    setEditModeTargetElement(newSentence.itemId);
    goTo(FILL_IN_BLANKS_SENTENCE);
  }, [
    id,
    sectionId,
    sortedSentences,
    properties,
    type,
    updateComponentProperties,
    setActiveSentence,
    setEditModeTargetElement,
    goTo,
  ]);

  const handleUpdateSentence = useCallback(
    (itemId: string, sort: string) => {
      const sentences = sortedSentences.map(s =>
        s.itemId === itemId
          ? {
              ...s,
              sort,
            }
          : s
      );
      updateComponentProperties({
        input: {
          componentId: id,
          sectionId,
          componentData: {
            properties: {
              fillInBlanks: {
                ...properties.fillInBlanks,
                sentences,
              },
            },
          },
        },
        properties: {
          ...properties,
          fillInBlanks: {
            ...properties.fillInBlanks,
            sentences,
          },
        },
        typename: 'ActivitiesFillInBlanksResponseComponent',
      });
    },
    [id, sectionId, sortedSentences, properties, updateComponentProperties]
  );

  const handleUpdateFont = useCallback(
    (fontFamily: FontFamily) => {
      updateComponentProperties({
        input: {
          componentId: id,
          sectionId,
          componentData: {
            properties: {
              fillInBlanks: {
                ...properties.fillInBlanks,
                fontFamily,
              },
            },
          },
        },
        properties: {
          ...properties,
          fillInBlanks: {
            ...properties.fillInBlanks,
            fontFamily,
          },
        },
        typename: 'ActivitiesFillInBlanksResponseComponent',
      });
    },
    [id, sectionId, properties, updateComponentProperties]
  );

  const handleUpdateFontSize = useCallback(
    (fontSize: string | null) => {
      updateComponentProperties({
        input: {
          componentId: id,
          sectionId,
          componentData: {
            properties: {
              fillInBlanks: {
                ...properties.fillInBlanks,
                fontSize,
              },
            },
          },
        },
        properties: {
          ...properties,
          fillInBlanks: {
            ...properties.fillInBlanks,
            fontSize,
          },
        },
        typename: 'ActivitiesFillInBlanksResponseComponent',
      });
    },
    [id, sectionId, properties, updateComponentProperties]
  );

  useEffect(() => {
    const sentence = sortedSentences.find(
      s => s.itemId === editModeTargetElement
    );
    if (sentence) {
      setActiveSentence({ itemId: sentence.itemId });
      goTo(FILL_IN_BLANKS_SENTENCE);
    } else {
      sortedSentences.forEach(sentence => {
        const answer = sentence.answerBlocks.find(
          a => a.itemId === editModeTargetElement
        );
        if (answer) {
          setActiveAnswer({ itemId: answer.itemId });
          setActiveSentence({ itemId: sentence.itemId });
          goTo(FILL_IN_BLANKS_SENTENCE_DETAILS);
          return;
        }
      });
    }
    // eslint-disable-next-line
  }, [editModeTargetElement, setActiveAnswer, setActiveSentence, goTo]);

  return (
    <ActivityBuilderDrawerWrapper>
      <ActivityBuilderDrawerHeader
        isBack={!!onCloseChildDrawer}
        title="Fill in the Blanks"
        onClose={onCloseChildDrawer ? onCloseChildDrawer : closeDrawer}
      />
      <ActivityBuilderDrawerContent ref={ref}>
        <SettingsGroup>
          <Box $alignItems="center" $justifyContent="space-between" $gap={8}>
            <Dropdown
              buttonElement={getFontFamilyLabel(fontFamily)}
              dropdownContents={
                <>
                  <RichtextToolbarDropdownItem
                    fontSize={fontSize16}
                    lineHeight={fontSize16LineHeight}
                    colour={grey10}
                    isSelected={fontFamily === FONT_CALIBRE_SCHOOL}
                    text="Calibre-R School"
                    onChange={() => handleUpdateFont(FONT_CALIBRE_SCHOOL)}
                  />
                  <RichtextToolbarDropdownItem
                    fontSize={fontSize16}
                    fontFamily={FONT_NSW_HANDWRITING}
                    lineHeight={fontSize16LineHeight}
                    colour={grey10}
                    isSelected={fontFamily === FONT_NSW_HANDWRITING}
                    text="NSW Handwriting"
                    onChange={() => handleUpdateFont(FONT_NSW_HANDWRITING)}
                  />
                </>
              }
            />

            <DropdownIcon
              tooltip="Text Size"
              buttonElement={<IconTextResize width={24} height={24} />}
              dropdownContents={
                <>
                  <RichtextToolbarDropdownItem
                    fontSize={16}
                    fontFamily={fontFamily}
                    lineHeight={20}
                    colour={grey10}
                    isSelected={
                      fontSize !== ACTIVITIES_OPTION_SIZE_LARGE &&
                      fontSize !== ACTIVITIES_OPTION_SIZE_HUGE &&
                      fontSize !== ACTIVITIES_OPTION_SIZE_MEDIUM
                    }
                    text="Small"
                    onChange={() => handleUpdateFontSize(null)}
                  />
                  <RichtextToolbarDropdownItem
                    fontSize={24}
                    fontFamily={fontFamily}
                    lineHeight={28}
                    colour={grey10}
                    isSelected={fontSize === ACTIVITIES_OPTION_SIZE_MEDIUM}
                    text="Medium"
                    onChange={() =>
                      handleUpdateFontSize(ACTIVITIES_OPTION_SIZE_MEDIUM)
                    }
                  />
                  <RichtextToolbarDropdownItem
                    fontSize={36}
                    fontFamily={fontFamily}
                    lineHeight={40}
                    colour={grey10}
                    isSelected={fontSize === ACTIVITIES_OPTION_SIZE_LARGE}
                    text="Large"
                    onChange={() =>
                      handleUpdateFontSize(ACTIVITIES_OPTION_SIZE_LARGE)
                    }
                  />
                  <RichtextToolbarDropdownItem
                    fontSize={48}
                    fontFamily={fontFamily}
                    lineHeight={52}
                    colour={grey10}
                    isSelected={fontSize === ACTIVITIES_OPTION_SIZE_HUGE}
                    text="Huge"
                    onChange={() =>
                      handleUpdateFontSize(ACTIVITIES_OPTION_SIZE_HUGE)
                    }
                  />
                </>
              }
            />
          </Box>
        </SettingsGroup>

        <SettingsHr />

        <PStyled marginBottom={4}>Sentences</PStyled>
        <SettingSmallText>
          Add one or multiple sentences with blanks.
        </SettingSmallText>

        {sentenceItemId && (
          <ActivityBuilderModal
            isVisible
            title="Delete Sentence"
            headerTitle="Delete Sentence"
            actions={
              <NegativeButton
                onClick={() => {
                  handleDeleteSentence(sentenceItemId);
                  setSentenceItemId(undefined);
                }}
                data-selector="modal-delete-sentence"
              >
                Delete Sentence
              </NegativeButton>
            }
            onClose={() => setSentenceItemId(undefined)}
          >
            <PStyled>Are you sure you want to delete this sentence?</PStyled>
          </ActivityBuilderModal>
        )}

        <SettingsGroup marginBottom={0}>
          <Sortable
            list={sortedSentences}
            renderItem={(item, index) => {
              const sourceItem = sortedSentences[index];
              return (
                <SortableItem key={item.itemId}>
                  <SortableHeader>
                    <SecondaryButtonIcon
                      type="button"
                      $isSmall
                      onClick={() => setSentenceItemId(item.itemId)}
                      data-selector="icon-delete-sentence"
                    >
                      <IconTrash width={20} height={20} />
                    </SecondaryButtonIcon>
                  </SortableHeader>
                  <SortableContentButton
                    type="button"
                    onClick={() => {
                      setActiveSentence({ itemId: item.itemId });
                      setEditModeTargetElement(item.itemId);
                      goTo(FILL_IN_BLANKS_SENTENCE);
                    }}
                  >
                    <Box $alignItems="center" $justifyContent="space-between">
                      <Styled.SettingsSentenceWrapper>
                        <BoldStyled marginBottom={4}>
                          Sentence {index + 1}
                        </BoldStyled>
                        <SentenceSummary sentence={sourceItem} />
                      </Styled.SettingsSentenceWrapper>
                      <Styled.SettingsSentenceIcon>
                        <IconDropdown width={16} height={16} rotate={-90} />
                      </Styled.SettingsSentenceIcon>
                    </Box>
                  </SortableContentButton>
                </SortableItem>
              );
            }}
            onSortChange={({ sourceIndex, destinationIndex, direction }) => {
              if (sourceIndex !== destinationIndex) {
                const item = sortedSentences[sourceIndex];
                const newSort = getReorderSort(
                  sortedSentences,
                  destinationIndex,
                  direction
                );
                handleUpdateSentence(item.itemId, newSort);
              }
            }}
          />
        </SettingsGroup>

        <SecondaryButton
          type="button"
          onClick={handleAddSentence}
          data-selector="btn-add-sentence"
        >
          Add Sentence
        </SecondaryButton>
      </ActivityBuilderDrawerContent>
    </ActivityBuilderDrawerWrapper>
  );
};

export default FillInBlanksSettingsSentences;

const SentenceSummary = ({ sentence }: { sentence: FillInBlanksSentence }) => {
  const mergedBlocks = useMemo(
    () => mergeFillInBlanksBlocks(sentence),
    [sentence]
  );

  const stringPreview = mergedBlocks
    .map(block =>
      'text' in block
        ? !isRichtextValueEmpty(block.text)
          ? generateText(
              block.text,
              getExtensions(BASE_FILL_IN_BLANKS_TEXT_BLOCK_SETTINGS)
            )
          : ''
        : `
          ${block.punctuation === '"' ? '“' : ''}_ _ _
          ${
            block.punctuation
              ? block.punctuation === '"'
                ? '”'
                : block.punctuation
              : ''
          }
        `
    )
    .join(' ');
  return <Styled.SettingsSentence>{stringPreview}</Styled.SettingsSentence>;
};
